import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export interface ExportUser {
  nome: string;
  email: string;
  telefone: string;
  dispositivo: string;
  tipoPlano: string;
  plano: string;
  statusChat: string;
  cadastro: string;
  ultimoAcesso: string;
  status: string;
}

const getPlanName = (planType: string): string => {
  const plans: Record<string, string> = {
    free: 'Gratuito',
    basic: 'Básico',
    pro: 'Profissional',
    premium: 'Premium'
  };
  return plans[planType] || planType;
};

const formatDateBR = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Nunca';
  try {
    return format(new Date(dateString), "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return 'Data inválida';
  }
};

export const prepareUsersForExport = (
  users: any[],
  getSubscription: (id: string) => any,
  getSession: (id: string) => any
): ExportUser[] => {
  return users.map(user => {
    const subscription = getSubscription(user.id);
    const session = getSession(user.id);
    const planType = subscription?.plan_type || 'free';
    
    // Determinar dispositivo
    let dispositivo = 'Desconhecido';
    if (session) {
      const browser = session.browser || '';
      const device = session.device || '';
      if (browser || device) {
        dispositivo = [browser, device].filter(Boolean).join(' / ');
      }
    }
    
    return {
      nome: user.display_name || 'Sem nome',
      email: user.email || '',
      telefone: user.phone || 'Não informado',
      dispositivo,
      tipoPlano: planType === 'free' ? 'Gratuito' : 'Pago',
      plano: getPlanName(planType),
      statusChat: user.status_chat !== false ? 'Liberado' : 'Bloqueado',
      cadastro: formatDateBR(user.created_at),
      ultimoAcesso: session?.last_active ? formatDateBR(session.last_active) : 'Nunca',
      status: session?.is_online ? 'Online' : 'Offline'
    };
  });
};

export const exportUsersToCSV = (data: ExportUser[]): void => {
  const headers = [
    'Nome',
    'E-mail',
    'Telefone',
    'Dispositivo',
    'Tipo Plano',
    'Plano',
    'Status Chat',
    'Cadastro',
    'Último Acesso',
    'Status'
  ];
  
  const csvContent = [
    headers.join(','),
    ...data.map(user => [
      `"${user.nome}"`,
      `"${user.email}"`,
      `"${user.telefone}"`,
      `"${user.dispositivo}"`,
      `"${user.tipoPlano}"`,
      `"${user.plano}"`,
      `"${user.statusChat}"`,
      `"${user.cadastro}"`,
      `"${user.ultimoAcesso}"`,
      `"${user.status}"`
    ].join(','))
  ].join('\n');
  
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  const dateStr = format(new Date(), 'yyyy-MM-dd');
  
  link.setAttribute('href', url);
  link.setAttribute('download', `usuarios-${dateStr}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportUsersToXLS = async (data: ExportUser[]): Promise<void> => {
  const XLSX = await import('xlsx');
  
  const wsData = [
    ['Nome', 'E-mail', 'Telefone', 'Dispositivo', 'Tipo Plano', 'Plano', 'Status Chat', 'Cadastro', 'Último Acesso', 'Status'],
    ...data.map(user => [
      user.nome,
      user.email,
      user.telefone,
      user.dispositivo,
      user.tipoPlano,
      user.plano,
      user.statusChat,
      user.cadastro,
      user.ultimoAcesso,
      user.status
    ])
  ];
  
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  
  // Definir largura das colunas
  ws['!cols'] = [
    { wch: 25 }, // Nome
    { wch: 30 }, // E-mail
    { wch: 15 }, // Telefone
    { wch: 20 }, // Dispositivo
    { wch: 12 }, // Tipo Plano
    { wch: 15 }, // Plano
    { wch: 12 }, // Status Chat
    { wch: 18 }, // Cadastro
    { wch: 18 }, // Último Acesso
    { wch: 10 }  // Status
  ];
  
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Usuários');
  
  const dateStr = format(new Date(), 'yyyy-MM-dd');
  XLSX.writeFile(wb, `usuarios-${dateStr}.xlsx`);
};

export const exportUsersToPDF = async (data: ExportUser[]): Promise<void> => {
  const { jsPDF } = await import('jspdf');
  
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });
  
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const dateStr = format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  
  // Header
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Justiçai - Relatório de Usuários', margin, 15);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Gerado em: ${dateStr}`, margin, 22);
  
  // Estatísticas
  const totalUsers = data.length;
  const paidUsers = data.filter(u => u.tipoPlano === 'Pago').length;
  const freeUsers = data.filter(u => u.tipoPlano === 'Gratuito').length;
  const onlineUsers = data.filter(u => u.status === 'Online').length;
  
  doc.setFontSize(9);
  doc.text(`Total: ${totalUsers} usuários | Pagos: ${paidUsers} | Gratuitos: ${freeUsers} | Online: ${onlineUsers}`, margin, 28);
  
  // Tabela
  const headers = ['Nome', 'E-mail', 'Dispositivo', 'Tipo', 'Plano', 'Chat', 'Cadastro', 'Últ. Acesso', 'Status'];
  const colWidths = [35, 55, 30, 18, 25, 18, 30, 30, 15];
  
  let y = 35;
  const rowHeight = 6;
  const headerHeight = 8;
  
  // Função para desenhar cabeçalho
  const drawHeader = () => {
    doc.setFillColor(59, 130, 246); // blue-500
    doc.rect(margin, y, pageWidth - margin * 2, headerHeight, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    
    let x = margin + 2;
    headers.forEach((header, i) => {
      doc.text(header, x, y + 5.5);
      x += colWidths[i];
    });
    
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    y += headerHeight;
  };
  
  drawHeader();
  
  // Dados
  data.forEach((user, index) => {
    if (y > pageHeight - 20) {
      doc.addPage();
      y = 15;
      drawHeader();
    }
    
    // Alternar cor de fundo
    if (index % 2 === 0) {
      doc.setFillColor(249, 250, 251); // gray-50
      doc.rect(margin, y, pageWidth - margin * 2, rowHeight, 'F');
    }
    
    doc.setFontSize(7);
    let x = margin + 2;
    
    const rowData = [
      user.nome.substring(0, 20),
      user.email.substring(0, 35),
      user.dispositivo.substring(0, 18),
      user.tipoPlano,
      user.plano,
      user.statusChat,
      user.cadastro,
      user.ultimoAcesso,
      user.status
    ];
    
    rowData.forEach((cell, i) => {
      doc.text(String(cell), x, y + 4);
      x += colWidths[i];
    });
    
    y += rowHeight;
  });
  
  // Rodapé
  const totalPages = doc.internal.pages.length - 1;
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Página ${i} de ${totalPages}`, pageWidth - margin - 25, pageHeight - 5);
  }
  
  const fileName = `usuarios-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
  doc.save(fileName);
};
